import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useGetAuthDetails } from '../../hooks/getAuthDetails';
import { BrandContext } from '../../contextProviders/brandContext';
import Loading from '../shared/Loading/Loading';
import { AUTHENTICATION_TYPES } from '../../constants/constants';
import { CSPUrlsMap } from '../../routes/CSPUrlsMap';
import { FeatureFlagContext } from '../../contextProviders/featureFlagContext';

const Login = ({ signup = false }) => {
  const navigate = useNavigate();
  const { authenticationType } =
    useContext(BrandContext).activeBrandContext.cspConfiguration.auth0Config[process.env.REACT_APP_ENV];
  const { cspConfiguration, isCspConfigLoading } = useContext(FeatureFlagContext);
  const { isCspAccessLoading, isAuth0Authenticated, loginWithRedirect, cspAccessToken, auth0AccessToken } =
    useGetAuthDetails();

  const isLiteModeEnabled = cspConfiguration?.IsLiteModeEnabled;

  useEffect(() => {
    const isFullyUnauthenticated = !cspAccessToken && !auth0AccessToken;

    // Login Flow 1: User has no CSP/Auth0 tokens (fresh visit or post-logout).
    // Send user to Auth0 immediately and do not wait for feature flags.
    if (isFullyUnauthenticated) {
      sessionStorage.clear();

      if (!signup) {
        loginWithRedirect();
      } else {
        loginWithRedirect({
          authorizationParams: {
            screen_hint: 'signup'
          }
        });
      }
      return;
    }

    // Login Flow 2: User has session context, but feature flags are still loading.
    // Pause until LaunchDarkly values are ready.
    if (isCspConfigLoading) return;

    // Login Flow 3: Lite mode user already authenticated in Auth0.
    // Bypass additional auth handoffs and continue directly to dashboard.
    if (isAuth0Authenticated && isLiteModeEnabled === true) {
      navigate(CSPUrlsMap.dashboard.url, { replace: true });
      return;
    }

    // Login Flow 4: SSO users require redirect handoff once auth state is stable.
    if (authenticationType === AUTHENTICATION_TYPES.SSO) {
      if (isAuth0Authenticated && !isCspAccessLoading) {
        loginWithRedirect();
      }
      return;
    }

    // Login Flow 5: Non-SSO user with valid session can proceed to dashboard.
    navigate(CSPUrlsMap.dashboard.url, { replace: true });
  }, [
    navigate,
    isCspAccessLoading,
    isAuth0Authenticated,
    loginWithRedirect,
    cspAccessToken,
    auth0AccessToken,
    authenticationType,
    signup,
    isLiteModeEnabled,
    isCspConfigLoading
  ]);

  return <Loading />;
};

export default Login;

Login.propTypes = {
  signup: PropTypes.bool
};
